import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Grid, Alert, Skeleton, Container,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useSelector } from 'react-redux';
import { selectSelectedStudent } from '../store/selectors/studentSelectors';
import {
  fetchStoreCatalog, CatalogItem, formatPaise, asText,
} from '../services/monetizationServices';

/**
 * The in-app store (#10).
 *
 * Ownership is taken from the API's `owned` flag and never inferred here. The
 * catalog already guarantees it reports nothing as owned while billing is off; a
 * client that second-guessed that — by checking entitlements separately, say —
 * could show an Open button for something the server will refuse, which is a worse
 * failure than showing a price.
 */
const StoreCard: React.FC<{ item: CatalogItem; onBuy: (sku: string) => void; onOpen: () => void }> =
  ({ item, onBuy, onOpen }) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flex: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {[asText(item.grade), asText(item.subject)].filter(Boolean).join(' · ')}
      </Typography>
      <Chip size="small" sx={{ mt: 1.5 }} label={`${item.questionCount} questions`} />
    </CardContent>
    <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      {item.owned ? (
        <>
          <Chip size="small" color="success" label="Owned" />
          <Button sx={{ ml: 'auto' }} variant="contained" onClick={onOpen}>Open</Button>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatPaise(item.amountPaise)}</Typography>
          <Button sx={{ ml: 'auto' }} variant="contained" onClick={() => onBuy(item.sku)}>
            Buy
          </Button>
        </>
      )}
    </Box>
  </Card>
);

const StorePage: React.FC = () => {
  const navigate = useNavigate();
  const selectedStudent = useSelector(selectSelectedStudent);
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'unauthorized'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchStoreCatalog();
      if (cancelled) return;
      if (result.state === 'ready') { setItems(result.items); setState('ready'); }
      else setState(result.state);
    })();
    return () => { cancelled = true; };
  }, []);

  const body = () => {
    if (state === 'loading') {
      return (
        <Grid container spacing={2}>
          {[0, 1, 2].map(i => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rounded" height={180} />
            </Grid>
          ))}
        </Grid>
      );
    }
    if (state === 'unauthorized') return <Alert severity="info">Sign in to see what is available.</Alert>;
    if (state === 'error') return <Alert severity="warning">The store could not be loaded just now.</Alert>;
    if (!items || items.length === 0) {
      return <Alert severity="info">There is nothing for sale yet. Check back before the next season.</Alert>;
    }
    return (
      <Grid container spacing={2}>
        {items.map(item => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.packageId}>
            <StoreCard
              item={item}
              onBuy={(sku) => navigate(`/checkout?sku=${encodeURIComponent(sku)}`)}
              onOpen={() => navigate('/dashboard')}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  // Header requires a student; without one there is nothing to scope the store to,
  // so send the visitor to pick a child rather than rendering a headerless page.
  if (!selectedStudent) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="info" action={
          <Button onClick={() => navigate('/select-student')}>Choose</Button>
        }>
          Choose a student to see the store.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header selectedStudent={selectedStudent} />
      <Container sx={{ py: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Store</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Practice packs you can add to your season.
        </Typography>
        {body()}
      </Container>
    </Box>
  );
};

export default StorePage;
